import React, { useState, useEffect, useRef } from "react";
import api from "../../utils/Api";
import { CSVLink } from "react-csv";
import { ArrowUp, ArrowDown, MapPin, Edit2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function Fichajes() {
  const today = new Date().toISOString().split("T")[0];

  const [fichajes, setFichajes] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    fechaInicio: today,
    fechaFin: today,
    empleado: "",
  });
  const [sortField, setSortField] = useState("fecha_hora");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [selectedFichaje, setSelectedFichaje] = useState(null);
  const [editHoras, setEditHoras] = useState({
    hora_entrada: "",
    hora_salida: "",
    hora_entrada_descanso: "",
    hora_fin_descanso: "",
    comentarios: "",
  });
  const [motivo, setMotivo] = useState("");
  const [toast, setToast] = useState({ message: "", type: "" });

  const perPage = 10;
  const debounceRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  const fetchFichajes = async (overrideFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        fecha_inicio: overrideFilters.fechaInicio || undefined,
        fecha_fin: overrideFilters.fechaFin || undefined,
        empleado: overrideFilters.empleado || undefined,
        sort_field: sortField,
        sort_order: sortOrder,
        page: currentPage,
        per_page: perPage,
      };
      const { data } = await api.get("/fichajes", { params });
      setFichajes({
        data: data.data || [],
        total: data.total || 0,
      });
    } catch (error) {
      console.error(error);
      showToast("Error al cargar los fichajes", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFichajes();
  }, [sortField, sortOrder, currentPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    if (name === "empleado") {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setCurrentPage(1);
        fetchFichajes({ ...filters, [name]: value });
      }, 300);
    }
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchFichajes();
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const totalPages = Math.ceil(fichajes.total / perPage) || 1;

  const openMap = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  // ----------------- PDF -----------------
  const exportPDF = async (dataToExport) => {
    let empresa = {};
    let imgData = null;

    try {
      const res = await api.get("/empresa"); 
      empresa = res.data;

      // Obtener logo en base64 desde backend
      const logoRes = await api.get("/empresa/logo");
      imgData = logoRes.data?.logo || null;
    } catch (err) {
      console.error("Error al obtener empresa o logo:", err);
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    if (imgData) {
      doc.addImage(imgData, "PNG", 40, 20, 100, 100);
    }

    doc.setFontSize(14);
    doc.setTextColor("#000000");
    doc.text(empresa.nombre_fiscal || "Empresa", 150, 40);
    doc.setFontSize(10);
    doc.text(`CIF: ${empresa.cif || "-"}`, 150, 55);
    doc.text(`Dirección: ${empresa.direccion || "-"}`, 150, 70);
    if (empresa.telefono) doc.text(`Teléfono: ${empresa.telefono}`, 150, 85);
    if (empresa.email) doc.text(`Email: ${empresa.email}`, 150, 100);

    doc.setFontSize(18);
    doc.setTextColor("#FF7A00");
    doc.text("Registro de Fichajes", 40, 130);

    const tableColumn = [
      "Empleado",
      "DNI",
      "Fecha",
      "Hora Entrada",
      "Hora Salida",
      "Inicio Descanso",
      "Fin Descanso",
      "Tipo",
      "Ubicación",
      "Incidencia",
    ];

    const tableRows = dataToExport.map((f) => [
      `${f.user?.nombre} ${f.user?.apellidos}`,
      f.user?.dni,
      f.fecha,
      f.hora_entrada || "-",
      f.hora_salida || "-",
      f.hora_entrada_descanso || "-",
      f.hora_fin_descanso || "-",
      f.tipo,
      f.ubicacion || "-",
      f.incidencia ? "Sí" : "No",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 150,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [255, 122, 0], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      theme: "striped",
      tableWidth: "auto",
      showHead: "everyPage",
    });

    doc.save(`fichajes_${Date.now()}.pdf`);
  };

  // ----------------- Edición de fichaje -----------------
  const openEditOffcanvas = (fichaje) => {
    setSelectedFichaje(fichaje);
    setEditHoras({
      hora_entrada: fichaje.hora_entrada || "",
      hora_salida: fichaje.hora_salida || "",
      hora_entrada_descanso: fichaje.hora_entrada_descanso || "",
      hora_fin_descanso: fichaje.hora_fin_descanso || "",
      comentarios: fichaje.comentarios || "",
    });
    setMotivo("");
    setOffcanvasOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditHoras((prev) => ({ ...prev, [name]: value }));
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!motivo || motivo.length < 5) {
      showToast("Debes ingresar un motivo válido (mínimo 5 caracteres)", "danger");
      return;
    }
    try {
      const payload = { ...editHoras, motivo };
      await api.put(`/fichajes/${selectedFichaje.id}/editar`, payload);
      setOffcanvasOpen(false);
      fetchFichajes();
      showToast("Fichaje actualizado correctamente", "success");
    } catch (err) {
      console.error(err);
      showToast("Error al actualizar fichaje", "danger");
    }
  };

  const getCamposPorTipo = (tipo) => {
    switch (tipo) {
      case "entrada":
        return ["hora_entrada", "comentarios"];
      case "salida":
        return ["hora_salida", "comentarios"];
      case "descanso":
        return ["hora_entrada_descanso", "hora_fin_descanso", "comentarios"];
      default:
        return ["comentarios"];
    }
  };

  // ----------------- Render -----------------
  return (
    <div className="container-fluid">
      <h3 className="fs-3 fw-bold">Registro de Fichajes</h3>

      {/* Toast */}
      {toast.message && (
        <div
          className={`toast align-items-center text-white bg-${toast.type} border-0 position-fixed top-0 end-0 m-3 show`}
          role="alert"
        >
          <div className="d-flex">
            <div className="toast-body">{toast.message}</div>
            <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast({ message: "", type: "" })}></button>
          </div>
        </div>
      )}

      {/* Filtros */}
      <form className="row g-3 mb-4" onSubmit={applyFilters}>
        <div className="col-md-3">
          <label className="form-label">Fecha Inicio</label>
          <input type="date" className="form-control" name="fechaInicio" value={filters.fechaInicio} onChange={handleFilterChange} />
        </div>
        <div className="col-md-3">
          <label className="form-label">Fecha Fin</label>
          <input type="date" className="form-control" name="fechaFin" value={filters.fechaFin} onChange={handleFilterChange} />
        </div>
        <div className="col-md-3">
          <label className="form-label">Empleado / DNI</label>
          <input type="text" className="form-control" name="empleado" placeholder="Nombre o DNI" value={filters.empleado} onChange={handleFilterChange} />
        </div>
        <div className="col-md-3 d-flex align-items-end">
          <button type="submit" className="btn w-100 rounded" style={{ color: "white" ,background: "#FF7A00"}}>Aplicar filtros</button>
        </div>
      </form>

      {/* Export CSV / PDF */}
      <div className="mb-3 text-end d-flex justify-content-end gap-2">
        <CSVLink
          data={fichajes.data.map(f => ({
            Empleado: `${f.user?.nombre} ${f.user?.apellidos}`,
            DNI: f.user?.dni,
            Fecha: f.fecha,
            Hora_Entrada: f.hora_entrada || "",
            Hora_Salida: f.hora_salida || "",
            Inicio_Descanso: f.hora_entrada_descanso || "",
            Fin_Descanso: f.hora_fin_descanso || "",
            Tipo: f.tipo,
            Ubicacion: f.ubicacion || "",
            Incidencia: f.incidencia ? "Sí" : "No",
            Comentarios: f.comentarios || "",
          }))}
          filename={`fichajes_${Date.now()}.csv`}
          className="btn rounded"
          style={{ color: "white" ,background: "#FF7A00"}}
        >
          Exportar CSV
        </CSVLink>
        <button className="btn btn-dark rounded" onClick={() => exportPDF(fichajes.data)} >Exportar PDF</button>
      </div>

      {/* Tabla */}
      {loading ? (
        <p>Cargando fichajes...</p>
      ) : fichajes.data.length === 0 ? (
        <p>No hay fichajes para los filtros aplicados.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Empleado</th>
                <th>DNI</th>
                <th onClick={() => toggleSort("fecha_hora")} style={{ cursor: "pointer" }}>Fecha {sortField === "fecha_hora" && (sortOrder === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}</th>
                <th>Hora Entrada</th>
                <th>Hora Salida</th>
                <th>Inicio Descanso</th>
                <th>Fin Descanso</th>
                <th>Tipo</th>
                <th>Ubicación</th>
                <th>Incidencia</th>
                <th>Editar</th>
              </tr>
            </thead>
            <tbody>
              {fichajes.data.map(f => (
                <tr key={f.id}>
                  <td>{f.user?.nombre} {f.user?.apellidos}</td>
                  <td>{f.user?.dni}</td>
                  <td>{f.fecha}</td>
                  <td>{f.hora_entrada || "-"}</td>
                  <td>{f.hora_salida || "-"}</td>
                  <td>{f.hora_entrada_descanso || "-"}</td>
                  <td>{f.hora_fin_descanso || "-"}</td>
                  <td>{f.tipo}</td>
                  <td>
                    {f.ubicacion ? <button className="btn btn-sm rounded" onClick={() => openMap(f.latitud, f.longitud)} style={{ color: "white" ,background: "#FF7A00"}} ><MapPin size={16}/> Ver</button> : "-"}
                  </td>
                  <td>{f.incidencia ? "Sí" : "No"}</td>
                  <td>
                    {f.incidencia && (
                      <button className="btn btn-success btn-sm rounded" onClick={() => openEditOffcanvas(f)}><Edit2 size={16}/> Editar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Paginación */}
         <nav>
          <ul style={{ listStyle: 'none', display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}>Anterior</button>
            </li>
            {[...Array(totalPages)].map((_, idx) => (
              <li key={idx} className={`page-item ${currentPage === idx + 1 ? "active" : ""}`}>
                <button
                  onClick={() => setCurrentPage(idx + 1)}
                  style={{
                    fontWeight: currentPage === idx + 1 ? "bold" : "normal",
                    textDecoration: currentPage === idx + 1 ? "underline" : "none"
                  }}
                >
                  {idx + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}>Siguiente</button>
            </li>
          </ul>
        </nav>

        </div>
      )}

      {/* Offcanvas de edición */}
      {offcanvasOpen && selectedFichaje && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={() => setOffcanvasOpen(false)}></div>
          <div className="offcanvas offcanvas-end show" style={{ visibility: "visible", width: "400px" }}>
            <div className="offcanvas-header bg-warning text-dark">
              <h5 className="offcanvas-title">Editar Fichaje ({selectedFichaje.tipo})</h5>
              <button type="button" className="btn-close" onClick={() => setOffcanvasOpen(false)}></button>
            </div>
            <div className="offcanvas-body">
              <form onSubmit={submitEdit}>
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_entrada") && (
                  <div className="mb-3">
                    <label className="form-label">Hora Entrada</label>
                    <input type="time" className="form-control" name="hora_entrada" value={editHoras.hora_entrada} onChange={handleEditChange} />
                  </div>
                )}
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_salida") && (
                  <div className="mb-3">
                    <label className="form-label">Hora Salida</label>
                    <input type="time" className="form-control" name="hora_salida" value={editHoras.hora_salida} onChange={handleEditChange} />
                  </div>
                )}
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_entrada_descanso") && (
                  <div className="mb-3">
                    <label className="form-label">Inicio Descanso</label>
                    <input type="time" className="form-control" name="hora_entrada_descanso" value={editHoras.hora_entrada_descanso} onChange={handleEditChange} />
                  </div>
                )}
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_fin_descanso") && (
                  <div className="mb-3">
                    <label className="form-label">Fin Descanso</label>
                    <input type="time" className="form-control" name="hora_fin_descanso" value={editHoras.hora_fin_descanso} onChange={handleEditChange} />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Comentarios</label>
                  <textarea className="form-control" name="comentarios" value={editHoras.comentarios} onChange={handleEditChange}></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">Motivo de la edición</label>
                  <textarea className="form-control" value={motivo} onChange={(e) => setMotivo(e.target.value)}></textarea>
                </div>
                <div className="d-flex justify-content-end">
                  <button type="button" className="btn btn-secondary me-2" onClick={() => setOffcanvasOpen(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-success">Guardar</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
